import React, { useMemo } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import {
  FormFileTransportProvider,
  useFormFillContext,
  type FormFileTransport,
} from '@eaco/forms-ui-web';
import type { DraftHandle } from './draftStorage';

/**
 * Public-form implementation of the package's `FormFileTransport` seam.
 * The shared upload/display element renderers live in
 * `@eaco/forms-ui-web` and call these three methods; here we back them
 * with the anonymous public-form GraphQL contract (publicSlug +
 * secretToken). The authenticated web host supplies its own
 * implementation of the same interface.
 *
 * Must be rendered inside a `FormFillContextProvider` so it can read the
 * current draft's `formDocumentResultId` / `secretToken` / `publicSlug`.
 */

const CREATE_FORM_RESULT_PRESIGNED_ATTACHMENT = gql`
  mutation createFormResultPresignedAttachment(
    $input: CreateFormResultPresignedAttachmentInput!
  ) {
    createFormResultPresignedAttachment(input: $input) {
      attachment {
        domain_id
        name
      }
      presigned_url
    }
  }
`;

const FORM_RESULT_FILE_QUERY = gql`
  query formResultFile(
    $id: ID!
    $fileId: ID!
    $secretToken: String
    $signoffToken: String
    $download: Boolean
  ) {
    formResultFile(
      id: $id
      fileId: $fileId
      secretToken: $secretToken
      signoffToken: $signoffToken
      download: $download
    ) {
      id
      name
      mimeType
      sizeBytes
      url
      thumbnailUrl
    }
  }
`;

const QUEUE_FILE_THUMBNAIL = gql`
  mutation queueFileThumbnail($fileId: ID!) {
    queueFileThumbnail(fileId: $fileId)
  }
`;

const PUBLIC_FORM_FILE_QUERY = gql`
  query publicFormFile($publicSlug: String!, $fileId: ID!) {
    publicFormFile(publicSlug: $publicSlug, fileId: $fileId) {
      id
      name
      mimeType
      sizeBytes
      url
      thumbnailUrl
    }
  }
`;

const SIZED_RESULT_FILE_QUERY = gql`
  query formResultFileSized(
    $id: ID!
    $fileId: ID!
    $secretToken: String
    $signoffToken: String
    $width: Int!
  ) {
    formResultFile(
      id: $id
      fileId: $fileId
      secretToken: $secretToken
      signoffToken: $signoffToken
    ) {
      id
      sizedUrl(width: $width)
    }
  }
`;

const SIZED_PUBLIC_FORM_FILE_QUERY = gql`
  query publicFormFileSized(
    $publicSlug: String!
    $fileId: ID!
    $width: Int!
  ) {
    publicFormFile(publicSlug: $publicSlug, fileId: $fileId) {
      id
      sizedUrl(width: $width)
    }
  }
`;

export const FormsFileTransportProvider: React.FC<{
  children: React.ReactNode;
  /**
   * Lazily create (or return the existing) anonymous draft this fill writes
   * into. Supplied by FormView so the first upload on an otherwise-untouched
   * form mints the draft on demand instead of erroring. Returns null when a
   * draft can't be started yet (e.g. an unsolved Turnstile challenge).
   */
  ensureDraft?: () => Promise<DraftHandle | null>;
  /**
   * Sign-off review link token. When set (the public /signoff/:token page),
   * result-file reads authorise against it instead of a filler secretToken —
   * so a reviewer sees the response's images even on internal fills.
   */
  signoffToken?: string | null;
}> = ({ children, ensureDraft, signoffToken = null }) => {
  const client = useApolloClient();
  const { formDocumentResultId, secretToken, publicSlug } =
    useFormFillContext();

  const transport = useMemo<FormFileTransport>(
    () => ({
      async ensureReady() {
        if (formDocumentResultId) return true;
        if (!ensureDraft) return false;
        return !!(await ensureDraft());
      },

      async createPresignedUpload({ filename, contentType, sizeBytes }) {
        // Resolve the live draft handle directly: ensureDraft() returns the
        // existing handle (from a ref) or creates one. We read its id/token
        // from the return value rather than the context, because when the
        // draft was just created in this same interaction the context hasn't
        // re-rendered yet and still carries the stale null id.
        const handle = ensureDraft ? await ensureDraft() : null;
        const id = handle?.resultId ?? formDocumentResultId;
        const token = handle?.secretToken ?? secretToken ?? null;
        if (!id) {
          throw new Error('Form is not ready to accept uploads yet');
        }
        const { data } = await client.mutate({
          mutation: CREATE_FORM_RESULT_PRESIGNED_ATTACHMENT,
          variables: {
            input: {
              id,
              secretToken: token,
              // A reviewer has no filler token; theirs is the sign-off link.
              signoffToken: signoffToken ?? null,
              filename,
              contentType,
              sizeBytes,
            },
          },
        });
        const payload = data?.createFormResultPresignedAttachment;
        const signedUrl = payload?.presigned_url;
        const fileId = payload?.attachment?.domain_id;
        if (!signedUrl || fileId == null) {
          throw new Error('Server did not return an upload URL');
        }
        return {
          signedUrl,
          fileId: Number(fileId),
          name: payload?.attachment?.name ?? filename,
        };
      },

      async fetchResultFileUrl(fileId, opts) {
        if (!formDocumentResultId) return null;
        const { data } = await client.query({
          query: FORM_RESULT_FILE_QUERY,
          variables: {
            id: formDocumentResultId,
            fileId: String(fileId),
            secretToken: secretToken ?? null,
            signoffToken: signoffToken ?? null,
            download: opts?.download ?? false,
          },
          fetchPolicy: 'network-only',
        });
        const f = data?.formResultFile;
        return f
          ? {
              name: f.name ?? null,
              url: f.url ?? null,
              thumbnailUrl: f.thumbnailUrl ?? null,
              mimeType: f.mimeType ?? null,
              sizeBytes: f.sizeBytes ?? null,
            }
          : null;
      },

      async fetchPublicFileUrl(fileId) {
        if (!publicSlug) return null;
        const { data } = await client.query({
          query: PUBLIC_FORM_FILE_QUERY,
          variables: { publicSlug, fileId: String(fileId) },
          fetchPolicy: 'cache-first',
        });
        const f = data?.publicFormFile;
        return f
          ? {
              name: f.name ?? null,
              url: f.url ?? null,
              thumbnailUrl: f.thumbnailUrl ?? null,
              mimeType: f.mimeType ?? null,
              sizeBytes: f.sizeBytes ?? null,
            }
          : null;
      },

      onUploaded(fileId) {
        // Fire-and-forget: generate the preview thumbnail for the new file.
        client
          .mutate({
            mutation: QUEUE_FILE_THUMBNAIL,
            variables: { fileId: String(fileId) },
          })
          .catch(() => {});
      },

      async fetchSizedUrl(fileId, width, opts) {
        // Design-time image (DisplayImage) → publicFormFile; answer → result.
        if (opts?.design) {
          if (!publicSlug) return null;
          const { data } = await client.query({
            query: SIZED_PUBLIC_FORM_FILE_QUERY,
            variables: { publicSlug, fileId: String(fileId), width },
            fetchPolicy: 'network-only',
          });
          return data?.publicFormFile?.sizedUrl ?? null;
        }
        if (!formDocumentResultId) return null;
        const { data } = await client.query({
          query: SIZED_RESULT_FILE_QUERY,
          variables: {
            id: formDocumentResultId,
            fileId: String(fileId),
            secretToken: secretToken ?? null,
            signoffToken: signoffToken ?? null,
            width,
          },
          fetchPolicy: 'network-only',
        });
        return data?.formResultFile?.sizedUrl ?? null;
      },
    }),
    [
      client,
      formDocumentResultId,
      secretToken,
      signoffToken,
      publicSlug,
      ensureDraft,
    ],
  );

  return (
    <FormFileTransportProvider value={transport}>
      {children}
    </FormFileTransportProvider>
  );
};
